import { encode } from "js-base64"
import { flow, getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  ChatTypes,
  Event,
  EventNames,
  FLAG_ACK,
  MessageStatus,
  MessageTypes,
  ChatPerms,
  MessageSearchData,
  MessageData,
  FLAG_REPLY,
  ChatData,
  MuteData,
  ChatBurningSwitchData,
} from "~proto"
import { staticUrl, buildGalleryImageKey } from "~utils"
import { isWebSocketDisconnectMessage } from "~utils/websocket-error"
import { ChatMemberModel } from "../chat-member"
import { ChannelChatInfoModel } from "./channel-chat-info"
import { Message, MessageModel } from "../message/message"
import { RootStoreModel } from "~models/root-store"
import { UserModel } from "../user/user"
import { NotifyType } from "~types/message"

/**
 * 聊天模型
 * 主要类别： 私聊，群聊
 *
 * TODO: 区分 ChatMember 职责，成员可以具备管理权限
 */

/**
 * 消息排序比较器（新消息在前，降序）。
 * 仅按 created 排序会在同一时间戳的消息上产生不稳定顺序：旧实现对相等时间戳总返回 1，
 * 排序结果依赖 self.messages 的插入顺序，而各端消息到达/同步顺序不同，导致“不同手机看到的消息顺序不一样”。
 * 这里以服务端主键 id（单调递增、各端一致）兜底，再以 message_id 兜底本地未落库消息，得到各端一致的全序。
 */
const compareMessagesDesc = (m1: Message, m2: Message) => {
  const t1 = m1.created.valueOf()
  const t2 = m2.created.valueOf()
  if (t1 !== t2) return t2 - t1
  const id1 = m1.id ?? 0
  const id2 = m2.id ?? 0
  if (id1 !== id2) return id2 - id1
  if (m1.message_id === m2.message_id) return 0
  return m1.message_id < m2.message_id ? 1 : -1
}

type ChatIdentity = { chat_id: string; id?: number | null; name?: string | null }

function buildChatIdentityKeys(chat: ChatIdentity): Set<string> {
  const keys = new Set<string>()
  if (chat.chat_id) keys.add(String(chat.chat_id))
  if (chat.id != null) keys.add(String(chat.id))
  if (chat.name) keys.add(String(chat.name))
  return keys
}

/** 群/频道消息可能带数字 id 或 WS cid，需与 chat 的多种标识交叉匹配 */
function messageMatchesChat(
  message: { chat_cid?: string | null; chat_id?: number | null },
  chat: ChatIdentity,
): boolean {
  if (!message.chat_cid && message.chat_id == null) return true
  if (message.chat_id != null && chat.id != null && message.chat_id === chat.id) {
    return true
  }
  if (message.chat_cid && buildChatIdentityKeys(chat).has(String(message.chat_cid))) {
    return true
  }
  return false
}

const ChatModelBase = types
  .model("Chat")
  .props({
    id: types.maybe(types.number), // 如果是用户与用户之间的对话，ID就是联系人用户ID
    chat_id: types.identifier,
    title: types.string,
    desc: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    image: types.maybeNull(types.string),
    members: types.optional(types.array(ChatMemberModel), []),
    type: types.optional(types.number, ChatTypes.PRIVATE),
    creator: types.maybe(types.safeReference(UserModel)),
    receiver: types.maybe(types.safeReference(UserModel)),
    messages: types.optional(types.array(types.safeReference(MessageModel)), []),
    updated: types.optional(types.Date, () => new Date()),
    created: types.optional(types.Date, () => new Date()),
    deleted: types.optional(types.boolean, false), // delete flag used for soft delete
    draft: types.maybe(types.string),
    pinTime: types.maybeNull(types.Date),
    is_mute: types.maybeNull(types.boolean),
    is_burning: types.maybeNull(types.boolean),
    burning_timer: types.maybeNull(types.number),
    channelChatInfo: types.maybe(ChannelChatInfoModel),
    pinned_message_id: types.maybeNull(types.number),
    pinned_message_content: types.maybeNull(types.string),
    /** 服务端批次先于明细同步时的未读叠加；真实消息入列后会 consumePackedUnreadHint */
    packedUnreadHint: types.optional(types.number, 0),
  })
  .volatile(() => ({
    typing: undefined,
    isSearching: false,
    isNoMoreData: false,
    all_mute: false,
    allow_history_msg: false, // 是否允许查看历史消息
  }))
  .views((self) => ({
    get isAllMuted() {
      return !!(self as any).all_mute
    },
    get sortedMessages() {
      const seen = new Set<string>()
      return Array.from(self.messages)
        .sort(compareMessagesDesc)
        .filter((m) => {
          if (!m?.message_id || seen.has(m.message_id)) return false
          if (!messageMatchesChat(m, self)) return false
          seen.add(m.message_id)
          return true
        })
    },
    // 获取所有的群成员会员资料
    get users() {
      return self.members.map(({ user }) => user)
    },
    get usersByName() {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const dst = {}
      self.members
        .map(({ user }) => user)
        .filter((user) => user.id !== userStore.currentUser.id)
        .forEach((user) => {
          const nameLetter = user.name?.substring(0, 1)
          dst[nameLetter] = dst[nameLetter] || []
          dst[nameLetter].push(user)
        })
      const lst = Object.keys(dst).map((letter) => ({ letter, data: dst[letter] }))
      return lst
    },
    // 显示标题
    get displayTitle() {
      if (self.type === ChatTypes.PRIVATE) {
        // 如果好友消息，优先显示联系人备注名称
        const { contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        const contact = self.receiver?.id ? contactStore.byUserId(self.receiver.id) : null
        return contact ? contact.displayName : self.receiver.name
      }
      return self.title
    },
    get displayUsername() {
      if (self.type === ChatTypes.PRIVATE) {
        return self.receiver?.username || ""
      }
      return self.name || ""
    },
    // 群成员数量
    get memebersCount() {
      return self.members.length
    },
    get allMessages() {
      return [...self.messages]
    },
    get lastMessage() {
      const sorted = self.sortedMessages
      return sorted.length ? sorted[0] : null
    },
    // 是否是游戏群
    get isGameGroup() {
      return [ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(self.type)
    },
    // 聊天类型枚举
    get chatTypeEnum(): "GROUP" | "CHANNEL" | "PRIVATE" {
      if ([ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(self.type)) {
        return "GROUP"
      } else if (self.type === ChatTypes.CHANNEL) {
        return "CHANNEL"
      } else {
        return "PRIVATE"
      }
    },
    get displayImage() {
      if (self.image) {
        return staticUrl(self.image)
      }
      return self.receiver ? self.receiver.img : ""
    },

    get messageCount() {
      return self.messages.length
    },

    get messageUnread() {
      return self.sortedMessages.filter(
        (message) => !message.byMe && !message.isReadedByMe && !message.notify_type,
      )
    },

    get messageUnreadCount() {
      return self.messageUnread.length + self.packedUnreadHint
    },

    // 所有图片（按时间从旧到新，供画廊左右滑动）
    get images() {
      const images: {
        type: MessageTypes
        message_id: string
        id?: number | null
        attach_path?: string | null
        url?: string | null
        key: string
      }[] = []
      const seenKeys = new Set<string>()
      const seenSingleMediaMessageIds = new Set<string>()
      const mediaTypes = [MessageTypes.IMAGE, MessageTypes.IMAGES, MessageTypes.VIDEO]

      const addImage = (item: (typeof images)[number]) => {
        if (!item.key || seenKeys.has(item.key)) return
        seenKeys.add(item.key)
        images.push(item)
      }

      // sortedMessages 已按 message_id 去重；再按 key 兜底，避免同图重复进画廊
      const chronological = [...self.sortedMessages].reverse()
      chronological.forEach((x) => {
        if (!mediaTypes.includes(x.type)) return

        if (x.type === MessageTypes.VIDEO || x.type === MessageTypes.IMAGE) {
          if (seenSingleMediaMessageIds.has(x.message_id)) return
          seenSingleMediaMessageIds.add(x.message_id)
          addImage({
            type: x.type,
            message_id: x.message_id,
            id: x.id,
            attach_path: x.attach_path,
            url: x.content,
            key: buildGalleryImageKey({
              message_id: x.message_id,
              attach_path: x.attach_path,
              content: x.content,
            }),
          })
          return
        }

        if (x.type === MessageTypes.IMAGES && x.attach?.imgs?.length) {
          if (seenSingleMediaMessageIds.has(x.message_id)) return
          seenSingleMediaMessageIds.add(x.message_id)
          x.attach.imgs.forEach((img) => {
            addImage({
              type: x.type,
              message_id: x.message_id,
              id: x.id,
              attach_path: img.uri,
              url: img.url,
              key: buildGalleryImageKey({
                message_id: x.message_id,
                attach_path: img.uri,
                url: img.url,
              }),
            })
          })
        }
      })
      return images
    },
    // 返回“除了当前用户以外”的所有群成员
    get membersExcludeSelf() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      return self.members?.filter(({ user_id }) => user_id !== currentUser.id)
    },
    // 当前登录用户是否还在这个聊天的成员列表里
    get isCurrentUserExistInMembers() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      return !!self.members?.find(({ user_id }) => user_id === currentUser.id)
    },
    // 判断当前登录账号是不是群主
    get isCurrentUserIsAdmin() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      return (
        self.members?.find(({ user_id }) => user_id === currentUser.id)?.perm === ChatPerms.ADMIN
      )
    },
    // 判断当前登录账号是否是群管理员
    get isCurrentUserIsManager() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)

      const member = self.members?.find(
        ({ user_id }) => user_id === currentUser.id
      )

      return [ChatPerms.SUB_ADMIN].includes(member?.perm)
    },
    /**
     * 判断当前登录账号是群里面的什么角色
     * 领红包账号: 领红包机器人(is_collect_bot)
     * 赔付账号: 赔付机器人(is_compensate_bot)
     * 代理: 代理(is_agent)
     * 普通成员: 普通成员(perm === ChatPerms.DEFAULT)
     * 群主: 群主(perm === ChatPerms.ADMIN)
     * 角色等级: 群主 > 赔付账号 > 领红包账号 > 代理 > 普通成员
     */
    get isCurrentUserRole() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      const member = self.members?.find(({ user_id }) => user_id === currentUser.id)
      if (!member) return "UNKNOWN"

      if (member.perm === ChatPerms.ADMIN) return "ADMIN"
      if (member.is_compensate_bot) return "COMPENSATE_BOT"
      if (member.is_collect_bot) return "COLLECT_BOT"
      if (member.is_agent) return "AGENT"
      if (member.perm === ChatPerms.DEFAULT) return "DEFAULT"
      // 兜底：例如 SUB_ADMIN 等扩展角色
      return "DEFAULT"
    },

    get isCurrentUserRoleLabel() {
      const role = (self as any).isCurrentUserRole as
        | "ADMIN"
        | "COMPENSATE_BOT"
        | "COLLECT_BOT"
        | "AGENT"
        | "DEFAULT"
        | "UNKNOWN"
      switch (role) {
        case "ADMIN":
          return "群主"
        case "COMPENSATE_BOT":
          return "赔付机器人"
        case "COLLECT_BOT":
          return "领红包机器人"
        case "AGENT":
          return "代理"
        case "DEFAULT":
          return "普通成员"
        default:
          return "未知"
      }
    },
    get isCurrentUserCanSpeak() {
      // 全群禁言开启时，仅群主与管理员可发言
      if (!(self as any).all_mute) return true
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      const member = self.members?.find(({ user_id }) => user_id === currentUser.id)
      return member?.perm === ChatPerms.ADMIN || member?.perm === ChatPerms.SUB_ADMIN
    },
    get groupQrcode() {
      const { configStore, userStore: { currentUser } } = getRoot<Instance<typeof RootStoreModel>>(self)
      let path = "group"
      if (self.type === ChatTypes.CHANNEL) {
        path = "channel"
      }
      const data = {
        path,
        id: self.id,
        chat_id: self.chat_id,
        sender_id: currentUser.id,
        title: self.title,
        image: self.image,
        memebersCount: self.members.length,
        isCurrentUserExistInMembers: !!self.members?.find(({ user_id }) => user_id === currentUser.id),
      }
      const b = encode(JSON.stringify(data))
      const dataQs = `path=${encodeURIComponent(path)}&data=${encodeURIComponent(b)}`
      const redirect = (configStore.redirectURL || "").trim()
      const appKey = (configStore as { appKey?: string }).appKey
      if (redirect) {
        const sep = redirect.includes("?") ? "&" : "?"
        const keyQs =
          appKey != null && String(appKey) !== "" ? `appKey=${encodeURIComponent(String(appKey))}&` : ""
        return `${redirect}${sep}${keyQs}${dataQs}`
      }
      // 扫码页只解析 path / data，不依赖 H5 域名与 appKey
      return `https://qrcode.internal/?${dataQs}`
    },
  }))
  .actions((self) => {
    const {
      messageStore,
      userStore: { currentUser: sender },
      eventStore,
    } = getRoot<Instance<typeof RootStoreModel>>(self)

    function setIsBurning(truthy: boolean) {
      self.is_burning = truthy
    }

    function setBurningTimer(second: number) {
      self.burning_timer = second
    }

    function setIsSearching(truthy: boolean) {
      self.isSearching = truthy
    }

    function setIsNoMoreData(truthy: boolean) {
      self.isNoMoreData = truthy
    }

    function setAllMute(truthy: boolean) {
      self.all_mute = truthy
    }

    function setAllowHistoryMsg(truthy: boolean) {
      self.allow_history_msg = truthy
    }

    function setID(id: number) {
      self.id = id
    }
    function setType(type: number) {
      self.type = type
    }
    function setCreator(id: number) {
      const {
        userStore: { users },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      self.creator = users.get(id.toString())
    }
    function setTitle(title: string) {
      self.title = title
    }
    function setImage(image: string) {
      self.image = image
    }
    function setDraft(value: string) {
      self.draft = value
    }
    function setName(name: string) {
      self.name = name
    }
    function clearDraft() {
      self.draft = undefined
    }
    function setTyping(user_id: number) {
      self.typing = user_id
    }
    function setPinTime() {
      self.pinTime = new Date()
    }
    function clearPinTime() {
      self.pinTime = null
    }
    function setIsMute(v: boolean) {
      self.is_mute = v
    }
    function setAnnouncement(text: string) {
      self.desc = text || ""
    }
    function setPinnedMessage(message_id: number | null, content: string | null) {
      self.pinned_message_id = message_id
      self.pinned_message_content = content
    }
    function membersToUsers(data: ChatData) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { members } = data
      const mdata = members.map((member) => {
        const { user, ...rest } = member
        const u = userStore.addUser(user)
        return { ...rest, user: u }
      })
      self.members.replace(mdata as any)
      return self
    }
    function prependMessages(historyMessages: Partial<Message>[]) {
      historyMessages.forEach((history) => {
        newHistoryMessage(history)
      })
    }
    function newHistoryMessage({
      content,
      type = MessageTypes.TEXT,
      mention_ids: mentionIDs = [],
      ...rest
    }: Partial<MessageData>): Message {
      // 历史消息必须保留原始 sender_id；SQLite 快照用 sender 数字字段，禁止默认成当前用户
      const m = messageStore.addMessage({
        content,
        type,
        chat_id: self.id,
        chat_cid: ["GROUP", "CHANNEL"].includes(self?.chatTypeEnum) ? self.chat_id : null,
        mention_ids: [...mentionIDs],
        burning_timer: self.burning_timer || 0,
        ...rest,
      })
      if (!self.messages.find((x) => x.message_id === m.message_id)) {
        self.messages.push(m)
      }
      return m
    }
    function newMessage({
      content,
      type = MessageTypes.TEXT,
      mention_ids: mentionIDs = [],
      ...rest
    }: Partial<MessageData> & { notify_type?: NotifyType }): Message {
      const m = messageStore.addMessage({
        content,
        type,
        chat_id: self.id,
        chat_cid: self.chat_id,
        sender_id: sender.id,
        receiver_id: self.receiver ? self.receiver.id : undefined,
        mention_ids: [...mentionIDs],
        burning_timer: self.burning_timer || 0,
        ...rest,
      })
      if (!self.messages.find((x) => x.message_id === m.message_id)) {
        self.messages.unshift(m)
      }
      return m
    }

    function update(values: Partial<Message>) {
      for (const [key, value] of Object.entries(values)) {
        if (key === "message") {
          newMessage({
            content: String(value ?? ""),
            type: MessageTypes.TEXT,
            status: MessageStatus.READ,
            notify_type: NotifyType.GROUP_TITLE_UPDATE,
          })
        }
        self[key] = value
      }
    }
    function addPackedUnreadHint(n = 1) {
      self.packedUnreadHint += n
    }
    function consumePackedUnreadHint(n = 1) {
      self.packedUnreadHint = Math.max(0, self.packedUnreadHint - n)
    }
    function clearPackedUnreadHint() {
      self.packedUnreadHint = 0
    }
    function clearLocalUnreadState() {
      // 游戏群未读可能只体现在 packedUnreadHint（GAME_CHAT_UNREAD_SUMMARY）上，没有已加载的未读消息
      const hadPackedHint = self.packedUnreadHint > 0
      clearPackedUnreadHint()
      const items: {
        sender_id: number
        message_id: string
        id: number | null
        status: MessageStatus
      }[] = []
      self.messages.forEach((message) => {
        if (message.byMe || message.notify_type || message.isReadedByMe) return
        message.markRead()
        const senderId = message.sender?.id
        if (senderId == null) return
        items.push({
          sender_id: senderId,
          message_id: message.message_id,
          id: message.id,
          status: MessageStatus.READ,
        })
      })
      return { items, hadPackedHint }
    }
    function markAllRead(options?: { report?: boolean }) {
      const report = options?.report !== false
      const { items, hadPackedHint } = clearLocalUnreadState()
      if (!report) return
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (items.length > 0) {
        const evt = new Event(EventNames.MESSAGE_BATCH_READ, {
          items,
        })
        eventStore.publish(evt, { flags: FLAG_ACK })
        const evt2 = new Event(EventNames.MESSAGE_BATCH_STATUS_UPDATED, {
          items,
        })
        eventStore.publish(evt2, { flags: FLAG_ACK })
      }
      if (items.length > 0 || hadPackedHint) {
        const evt3 = new Event(EventNames.CHAT_READ_REPORT, {
          chat_id: self.id,
        })
        eventStore.publish(evt3, { flags: FLAG_ACK })
      }
    }

    /**
     * 非当前会话的内存淘汰：只保留最新 keep 条，返回被淘汰的 message_id（由调用方从 messageStore 全局缓存销毁）。
     * - 保留窗口内消息 reply_msg_id 指向的旧消息一并保留，避免回复预览断链；
     * - 被淘汰的未读折算进 packedUnreadHint，角标不变（进群 markAllRead 会统一清理；
     *   consumePackedUnreadHint 当前无调用点，不存在双重扣减）；
     * - 内存淘汰不影响持久化：普通群重进从 SQLite 读回，游戏群重进直接拉服务端最新一屏。
     */
    function trimToLatest(keep: number): string[] {
      const live = Array.from(self.messages).filter(Boolean) as Message[]
      if (live.length <= keep) return []
      const sorted = live.slice().sort(compareMessagesDesc)
      const kept = sorted.slice(0, keep)
      const keptIds = new Set(kept.map((m) => m.message_id))
      // 保住保留窗口内的回复目标
      kept.forEach((m) => {
        if (m.reply_msg_id) keptIds.add(String(m.reply_msg_id))
      })
      const evicted = sorted.slice(keep).filter((m) => !keptIds.has(m.message_id))
      if (!evicted.length) return []
      const evictedUnread = evicted.filter(
        (m) => !m.byMe && !m.isReadedByMe && !m.notify_type,
      ).length
      if (evictedUnread > 0) {
        self.packedUnreadHint += evictedUnread
      }
      self.messages.replace(live.filter((m) => keptIds.has(m.message_id)))
      return evicted.map((m) => m.message_id)
    }

    /**
     * GAME_CHAT_UNREAD_SUMMARY：与在同一轮循环里反复调用 onMessageCreated(prepend=false) 等价
     *（每条 unshift）；一次写入前缀避免 O(batch×列表长度)。
     */
    function prependUnreadSummaryBatch(messages: Message[]) {
      const owned = messages.filter((m) => m && messageMatchesChat(m, self))
      if (!owned.length) return
      const reviveDeletedChat = true
      if (reviveDeletedChat && self.deleted) {
        self.deleted = false
      }
      const existing = new Set(self.messages.map((x) => x.message_id))
      const idsToPrepend: string[] = []
      for (let i = owned.length - 1; i >= 0; i--) {
        const mid = owned[i]?.message_id
        if (mid == null || existing.has(mid)) continue
        idsToPrepend.push(mid)
        existing.add(mid)
      }
      if (!idsToPrepend.length) return
      self.messages.unshift(...idsToPrepend)
    }

    //  消息创建
    function onMessageCreated(
      message: Message,
      prepend?: boolean,
      options?: { reviveDeletedChat?: boolean },
    ) {
      const { message_id, chat_cid } = message
      if (chat_cid && !messageMatchesChat(message, self)) return
      // `deleted` 是本机软删除标记：
      // - 历史/最后一条消息同步（prepend=true）不应“复活”已删除会话，否则会覆盖 chat-store 的 deletedChatIds 恢复逻辑
      // - 实时新消息（prepend=false）默认允许复活
      const reviveDeletedChat = options?.reviveDeletedChat ?? !prepend
      if (reviveDeletedChat && self.deleted) {
        self.deleted = false
      }
      if (!self.messages.find((x) => x.message_id === message_id)) {
        if (prepend) {
          self.messages.push(message_id)
        } else {
          self.messages.unshift(message_id)
        }
      }
    }

    function onMessageDeleted({ message_id }: Message) {
      const index = self.messages.findIndex((x) => {
        return x.message_id === message_id
      })
      if (index >= 0) {
        self.messages.splice(index, 1)
      }
    }

    function onMessagesBatchDeleted(messageIds: Set<string>) {
      if (!messageIds.size) return
      const remaining = self.messages.filter((message) => !messageIds.has(message.message_id))
      if (remaining.length !== self.messages.length) {
        self.messages.replace(remaining)
      }
    }

    /** 将 messageStore 中已存在但未挂到本会话引用的群消息补挂回来（cid/id 不一致时的自愈） */
    function relinkMessagesFromStore() {
      const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const linked = new Set(self.messages.map((m) => m.message_id))
      messageStore.messages.forEach((msg) => {
        if (!msg?.message_id || linked.has(msg.message_id)) return
        if (!messageMatchesChat(msg, self)) return
        onMessageCreated(msg, true, { reviveDeletedChat: false })
        linked.add(msg.message_id)
      })
    }

    /** 移除误挂到本会话的其他群消息引用 */
    function pruneForeignMessages() {
      const kept = self.messages.filter((m) => m && messageMatchesChat(m, self))
      if (kept.length !== self.messages.length) {
        self.messages.replace(kept)
      }
    }

    // 清除消息
    function clearMessages() {
      const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const ids = self.messages.map((x) => x.message_id)
      self.messages.forEach((message) => {
        messageStore.handleMessageDeleted({ message_id: message.message_id, id: message.id })
      })
      self.messages.clear()
      ids.forEach((x) => messageStore.remove(x))
    }

    function setDeleted(b: boolean) {
      try {
        self.deleted = b
      } catch (error) {
      }
    }

    // 获取聊天记录
    const refreshChatListFromServer = flow(function* refreshChatListFromServer() {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      yield new Promise<void>((resolve, reject) => {
        const evt = new Event(EventNames.CHAT_LIST, {})
        eventStore.publish(evt, {
          flags: FLAG_REPLY,
          onData: () => resolve(),
          onError: (err: Error) => reject(err),
        })
      })
    })

    function ensureServerChatId(): number | null {
      if (self.id != null && self.id > 0) return self.id

      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const candidates = [self.chat_id, self.name].filter(Boolean).map(String)
      for (const cid of candidates) {
        const found =
          chatStore.getChatByMessageRef?.({ chat_cid: cid, chat_id: null }) ??
          chatStore.byChatId?.(cid) ??
          chatStore.chats?.get?.(cid)
        if (found?.id != null && found.id > 0) {
          self.setID(found.id)
          if (found.name && !self.name) self.setName(found.name)
          if (found.type != null && found.type !== self.type) self.setType(found.type)
          return found.id
        }
      }
      return self.id ?? null
    }

    const publicReadChatHistory = flow(function* (
      id: number | null,
      options?: { replace?: boolean; retried?: boolean; manageSearching?: boolean },
    ) {
      const manageSearching = options?.manageSearching !== false
      // console.log("publicReadChatHistory")
      const limit = 30
      const data: MessageSearchData = {
        id_lt: id,
        limit,
      }
      const serverChatId = ensureServerChatId()
      if (!serverChatId) {
        if (!options?.retried) {
          try {
            yield refreshChatListFromServer()
            if (ensureServerChatId()) {
              yield publicReadChatHistory(id, { ...options, retried: true })
            } else {
              setIsNoMoreData(true)
            }
            return
          } catch {
            setIsNoMoreData(true)
            return
          }
        }
        setIsNoMoreData(true)
        return
      }

      const { websocketStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (!websocketStore.ready) {
        if (self.sortedMessages.length === 0) {
          setIsNoMoreData(true)
        }
        return
      }

      data.chat_id = serverChatId
      data.target_user_id = ["GROUP", "CHANNEL"].includes(self?.chatTypeEnum) ? null : self.receiver?.id

      try {
        if (manageSearching) setIsSearching(true)
        const evt = new Event<MessageSearchData>(EventNames.MESSAGE_SEARCH, data)
        const v = yield new Promise((resolve, reject) => {
          eventStore.publish(evt, {
            flags: FLAG_REPLY,
            onData(data) {
              resolve(data)
            },
            onError(error) {
              reject(error)
            },
          })
        })
        if (v) {
          const items = (v.items as MessageData[]) ?? []
          items.forEach((item) => {
            messageStore.onHistoryMessageCreated(item)
          })
          // replace：游戏群进群刷新「最新一屏」时，裁掉比本次窗口更旧的残留消息
          //（登录时 GAME_CHAT_UNREAD_SUMMARY / 最后一条 种进来的旧块），
          // 消除旧块与最新窗口之间的时间断层。
          // 故意「先拉成功、再按时间下界裁剪、且只删更旧的」：
          //   - 网络失败不进此分支，列表保持原样、不会被清空；
          //   - 只删严格更旧的消息，拉取期间到达的实时新消息（更新、在下界之上）不会被误删。
          if (options?.replace && items.length > 0) {
            const insertedIds = new Set(items.map((it) => it.message_id).filter(Boolean))
            let boundary = Infinity
            self.messages.forEach((m) => {
              if (m && insertedIds.has(m.message_id)) {
                boundary = Math.min(boundary, m.created.valueOf())
              }
            })
            if (boundary !== Infinity) {
              const remaining = self.messages.filter((m) => m && m.created.valueOf() >= boundary)
              if (remaining.length !== self.messages.length) {
                self.messages.replace(remaining)
              }
            }
          }
          setIsNoMoreData(items.length < limit)
        } else {
          setIsNoMoreData(true)
        }
      } catch (error) {
        const msg = String((error as Error)?.message ?? "")
        const missingChat =
          msg.includes("频道不存在") ||
          msg.includes("群聊不存在") ||
          msg.includes("聊天室不存在")
        if (missingChat && !options?.retried) {
          try {
            yield refreshChatListFromServer()
            if (ensureServerChatId()) {
              yield publicReadChatHistory(id, { ...options, retried: true })
              return
            }
          } catch {
            // fall through
          }
        }
        if (
          msg === "没有更多历史纪录" ||
          msg === "没有更多历史记录" ||
          msg === "超时未响应, 请重试" ||
          isWebSocketDisconnectMessage(msg)
        ) {
          setIsNoMoreData(true)
        } else if (self.sortedMessages.length === 0) {
          setIsNoMoreData(true)
        }
        // console.log(error, '聊天记录获取失败')
      } finally {
        if (manageSearching) setIsSearching(false)
      }
    })

    const publicToggleMuteChat = flow(function* (is_mute: boolean) {
      const data: MuteData = {
        is_mute,
      }
      if (self.receiver) {
        data.user_id = self.receiver.id === sender.id ? self.creator.id : self.receiver.id
      } else if (self.id) {
        data.chat_id = self.id
      }
      const evt = new Event<MuteData>(EventNames.MUTE, data)
      eventStore.publish(evt, {
        flags: FLAG_ACK,
      })
    })

    const publicSwitchBurningChat = flow(function* (is_burning: boolean, burning_timer: number) {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      self.is_burning = is_burning
      self.burning_timer = burning_timer
      const data: ChatBurningSwitchData = {
        is_burning: is_burning,
        burning_timer: burning_timer,
        creator_id: currentUser.id,
        receiver_id: currentUser.id === self.creator.id ? self.receiver.id : self.creator.id,
        chat_id: self.id,
        chat_cid: self.chat_id,
      }
      const evt = new Event<ChatBurningSwitchData>(EventNames.CHAT_BURNING_SWITCH, data)
      eventStore.publish(evt, {
        flags: FLAG_ACK,
      })
    })
    const setChannelChatInfo = (data: Record<string, unknown>) => {
      try {
        const normalized = {
          ...data,
          chat_id: Number(data.chat_id ?? self.id),
          official_balance_cny:
            data.official_balance_cny ??
            data.official_balance ??
            data.balance_cny,
        }
        if (!self.channelChatInfo) {
          self.channelChatInfo = ChannelChatInfoModel.create(normalized as never)
        } else {
          self.channelChatInfo.update(normalized as never)
        }
      } catch (e) {
        // console.log(e, 'setChannelChatInfo error')
      }
    }
    

    return {
      setIsBurning,
      setBurningTimer,
      membersToUsers,
      setIsSearching,
      setIsNoMoreData,
      setAllMute,
      setAllowHistoryMsg,
      setID,
      setType,
      setTitle,
      setCreator,
      setImage,
      setDraft,
      clearDraft,
      setTyping,
      setPinTime,
      setName,
      clearPinTime,
      setIsMute,
      setAnnouncement,
      setPinnedMessage,
      prependMessages,
      trimToLatest,
      newHistoryMessage,
      newMessage,
      update,
      clearMessages,
      relinkMessagesFromStore,
      pruneForeignMessages,
      markAllRead,
      prependUnreadSummaryBatch,
      onMessageCreated,
      onMessageDeleted,
      onMessagesBatchDeleted,
      setDeleted,
      addPackedUnreadHint,
      consumePackedUnreadHint,
      clearPackedUnreadHint,
      publicReadChatHistory,
      publicToggleMuteChat,
      publicSwitchBurningChat,
      setChannelChatInfo
    }
  })

export const ChatModel = types.snapshotProcessor(ChatModelBase, {
  preProcessor(snapshot) {
    if (!snapshot || typeof snapshot !== "object") return snapshot as any
    const s: any = snapshot
    // Backend sometimes sends `false` for desc; normalize to null for MST.
    if (s.desc === false) return { ...s, desc: null }
    if (s.desc != null && typeof s.desc !== "string") return { ...s, desc: String(s.desc) }
    return snapshot as any
  },
})

export type Chat = Instance<typeof ChatModel>
type ChatSnapshotType = SnapshotOut<typeof ChatModel>
export interface ChatSnapshot extends ChatSnapshotType {}
export const createChatDefaultModel = () => types.optional(ChatModel, {})
